import React, { useState, useEffect } from 'react';
import ProjectFinancials from './ProjectFinancials';

function ProjectDetails({ projectData }) {
  const [financialData, setFinancialData] = useState({
    originalContractAmount: 0,
    approvedContractCOs: 0,
    currentContractAmount: 0,
    potentialCOsAmount: 0
  });

  useEffect(() => {
    const fetchFinancialData = async () => {
      if (!projectData.projectId || !projectData.division) return;

      try {
        const response = await fetch(
          `https://projects-dev.nettlescs.com:5000/api/finicial-budget?projectID=${projectData.projectId}&divisionName=${projectData.division}`
        );
        const data = await response.json();

        // Find the values from budgetProgressData array
        const findValue = (label) => {
          const item = data.budgetProgressData.find(item => item.label === label);
          return item ? item.value : 0;
        };

        setFinancialData({
          originalContractAmount: findValue("Original Contract Amount"),
          approvedContractCOs: findValue("Approved Contract COs"),
          currentContractAmount: findValue("Current Contract Amount"),
          potentialCOsAmount: findValue("Potential COs Amount")
        });
      } catch (error) {
        console.error('Error fetching financial data:', error);
      }
    };

    fetchFinancialData();
  }, [projectData.projectId, projectData.division]);

  // Helper function to format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  return (
    <main>
      <div className="flex gap-4">
        {/* Left Section */}
        <section className="flex-1 bg-[#e5e7eb] p-6 rounded-lg shadow-[0_2px_4px_rgba(0,0,0,0.1)]">
          <div className="flex flex-col">
            {/* First Row - Project Name (75%) and Branch (25%) */}
            <div className="flex">
              {/* Project Name - 75% */}
              <div className="relative group w-[70%] pr-4">
                <div className="text-gray-600 mb-1">Project Name:</div>
                <div className="text-lg text-red-700 font-medium truncate" title={projectData.projectName || '-'}>
                  {projectData.projectName || '-'}
                </div>
                <div className="invisible group-hover:visible absolute z-10 bg-gray-900 text-white p-2 rounded text-sm whitespace-normal max-w-xs -translate-y-full top-0">
                  {projectData.projectName || '-'}
                </div>
                <div className="absolute right-0 top-0 h-full w-px bg-gray-400"></div>
              </div>

              {/* Branch - 25% */}
              <div className="relative group w-[30%] pl-4">
                <div className="text-gray-600 mb-1">Branch:</div>
                <div className="text-lg text-zinc-800 truncate" title={projectData.company || '-'}>
                  {projectData.company || '-'}
                </div>
                <div className="invisible group-hover:visible absolute z-10 bg-gray-900 text-white p-2 rounded text-sm whitespace-normal max-w-xs -translate-y-full top-0">
                  {projectData.company || '-'}
                </div>
              </div>
            </div>

            <div className="h-px bg-gray-400 my-4" />

            {/* Second Row */}
            <div className="flex">
              {/* Scope - 35% */}
              <div className="relative group w-[35%] pr-4">
                <div className="text-gray-600 mb-1">Scope:</div>
                <div className="text-lg text-zinc-800 truncate" title={projectData.scope || '-'}>
                  {projectData.scope || '-'}
                </div>
                <div className="invisible group-hover:visible absolute z-10 bg-gray-900 text-white p-2 rounded text-sm whitespace-normal max-w-xs -translate-y-full top-0">
                  {projectData.scope || '-'}
                </div>
                <div className="absolute right-0 top-0 h-full w-px bg-gray-400"></div>
              </div>

              {/* Delivery Method - 35% */}
              <div className="relative group w-[35%] px-4">
                <div className="text-gray-600 mb-1">Delivery Method:</div>
                <div className="text-lg text-zinc-800 truncate" title={projectData.deliveryMethod || '-'}>
                  {projectData.deliveryMethod || '-'}
                </div>
                <div className="invisible group-hover:visible absolute z-10 bg-gray-900 text-white p-2 rounded text-sm whitespace-normal max-w-xs -translate-y-full top-0">
                  {projectData.deliveryMethod || '-'}
                </div>
                <div className="absolute right-0 top-0 h-full w-px bg-gray-400"></div>
              </div>

              {/* Project No - 15% */}
              <div className="relative group w-[15%] px-4">
                <div className="text-gray-600 mb-1">Project No:</div>
                <div className="text-lg text-zinc-800 truncate" title={projectData.projectNo || '-'}>
                  {projectData.projectNo || '-'}
                </div>
                <div className="invisible group-hover:visible absolute z-10 bg-gray-900 text-white p-2 rounded text-sm whitespace-normal max-w-xs -translate-y-full top-0">
                  {projectData.projectNo || '-'}
                </div>
                <div className="absolute right-0 top-0 h-full w-px bg-gray-400"></div>
              </div>

              {/* Opportunity No - 15% */}
              <div className="relative group w-[15%] pl-4">
                <div className="text-gray-600 mb-1">Opportunity No:</div>
                <div className="text-lg text-zinc-800 truncate" title={projectData.opportunityNo || '-'}>
                  {projectData.opportunityNo || '-'}
                </div>
                <div className="invisible group-hover:visible absolute z-10 bg-gray-900 text-white p-2 rounded text-sm whitespace-normal max-w-xs -translate-y-full top-0">
                  {projectData.opportunityNo || '-'}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Right Section (Financials) */}
        <aside className="w-1/3 bg-white p-6 rounded-lg">
          <div className="flex flex-col">
            <div className="flex justify-between items-center mb-[0.35rem]">
              <div className="text-zinc-800">Org. Contract Amt:</div>
              <div className="text-zinc-800">{formatCurrency(financialData.originalContractAmount)}</div>
            </div>
            <div className="flex justify-between items-center mb-[0.35rem]">
              <div className="text-zinc-800">Approx. Contract COs:</div>
              <div className="text-zinc-800">{formatCurrency(financialData.approvedContractCOs)}</div>
            </div>
            <div className="h-px bg-gray-200 my-3" />
            <div className="flex justify-between items-center mb-3 font-bold">
              <div className="text-zinc-800">Current Contract Amt:</div>
              <div className="text-zinc-800">{formatCurrency(financialData.currentContractAmount)}</div>
            </div>
            <div className="flex justify-between items-center italic">
              <div className="text-zinc-800">Budgeted COs Amt:</div>
              <div className="text-zinc-800">{formatCurrency(financialData.potentialCOsAmount)}</div>
            </div>
          </div>
        </aside>
      </div>
    </main>
  );
}

export default ProjectDetails;